import React, { useMemo, useCallback } from 'react';
import moment from 'moment';
import { Table, Tooltip, Button, Card, Row, Col, Icon, Badge, Tag, Progress } from 'antd';

const tableStyles = `
@keyframes po-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
@keyframes po-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes po-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes po-shine  { 0%{opacity:0;transform:translateX(-100%)} 50%{opacity:0.5} 100%{opacity:0;transform:translateX(100%)} }

.po-row-stripe td{background:#f7f9ff!important}
.po-badge-status{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700;white-space:nowrap}
.po-dot-status{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.po-status-open{background:#f6ffed;border:1px solid #b7eb8f;color:#52c41a}
.po-status-open .po-dot-status{background:#52c41a;box-shadow:0 0 5px #52c41a}
.po-status-served{background:#e6f7ff;border:1px solid #91d5ff;color:#1890ff}
.po-status-served .po-dot-status{background:#1890ff;box-shadow:0 0 5px #1890ff}

.po-action-btn{width:32px!important;height:32px!important;min-width:32px!important;padding:0!important;border-radius:8px!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-size:14px!important;border:1.5px solid #d6e8ff!important;background:#e6f7ff!important;color:#1890ff!important;transition:all .25s!important}
.po-action-btn:hover{transform:translateY(-2px)!important;box-shadow:0 4px 12px rgba(24,144,255,.25)!important;background:#1890ff!important;color:#fff!important;border-color:#1890ff!important}

.po-delivery-progress{display:flex;align-items:center;gap:8px}
.po-delivery-text{font-size:12px;font-weight:600;white-space:nowrap}
.po-progress-bar{flex:1;min-width:120px}
`;

// Memoized StatusBadge component
const StatusBadge = React.memo(({ status }) => {
  const statusConfig = useMemo(() => {
    const map = {
      'OPEN':   { cls: 'po-status-open',   label: 'OPEN',   icon: 'unlock' },
      'SERVED': { cls: 'po-status-served', label: 'SERVED', icon: 'check-circle' },
    };
    return map[status] || { cls: 'po-status-open', label: status, icon: 'info-circle' };
  }, [status]);
  
  return (
    <span className={`po-badge-status ${statusConfig.cls}`}>
      <span className="po-dot-status" />
      <Icon type={statusConfig.icon} style={{ fontSize: 12 }} />
      {statusConfig.label}
    </span>
  );
});

// Memoized DeliveryProgress component
const DeliveryProgress = React.memo(({ totalItems, totalDelivered, totalQuantity }) => {
  const deliveryStats = useMemo(() => {
    const deliveredPercentage = totalQuantity > 0 ? (totalDelivered / totalQuantity) * 100 : 0;
    const color = deliveredPercentage === 100 ? '#52c41a' : deliveredPercentage >= 50 ? '#fa8c16' : '#1890ff';
    return { deliveredPercentage, color };
  }, [totalQuantity, totalDelivered]);
  
  return (
    <div className="po-delivery-progress">
      <span className="po-delivery-text">
        {totalItems} item{totalItems !== 1 ? 's' : ''}
      </span>
      <Progress 
        percent={Math.round(deliveryStats.deliveredPercentage)} 
        size="small" 
        strokeColor={deliveryStats.color}
        showInfo={false}
        className="po-progress-bar"
      />
      <span className="po-delivery-text">
        {totalDelivered}/{totalQuantity}
      </span>
    </div>
  );
});

const PurchaseOrderTable = ({
  poList,
  customers,
  pageLength,
  paginate,
  onSizeChange,
  onPageChange,
  openModal,
}) => {
  // Memoized statistics
  const stats = useMemo(() => {
    if (!poList || !poList.length) return { total: 0, open: 0, served: 0, totalItems: 0, totalValue: 0 };
    
    return {
      total: poList.length,
      open: poList.filter(po => po.status === 'OPEN').length,
      served: poList.filter(po => po.status === 'SERVED').length,
      totalItems: poList.reduce((sum, po) => sum + (po.totalItems || 0), 0),
      totalValue: poList.reduce((sum, po) => sum + (po.totalValue || 0), 0),
    };
  }, [poList]);
  
  // Memoized stat cards
  const statCards = useMemo(() => {
    return [
      { title: 'Total Orders', value: stats.total, icon: 'file-text', color: '#1890ff', bg: '#e6f7ff', gc: 'rgba(24,144,255,0.3)' },
      { title: 'Open Orders', value: stats.open, icon: 'unlock', color: '#52c41a', bg: '#f6ffed', gc: 'rgba(82,196,26,0.3)' },
      { title: 'Served Orders', value: stats.served, icon: 'check-circle', color: '#1890ff', bg: '#e6f7ff', gc: 'rgba(24,144,255,0.3)' },
      { title: 'Total Items', value: stats.totalItems, icon: 'shopping-cart', color: '#fa8c16', bg: '#fff7e6', gc: 'rgba(250,140,22,0.3)' },
    ];
  }, [stats.total, stats.open, stats.served, stats.totalItems]);
  
  // Memoized card style
  const cardStyle = useMemo(() => ({ 
    borderRadius: 10, 
    boxShadow: '0 2px 12px rgba(24,144,255,0.08)', 
    border: '1px solid #e6f0ff', 
    background: '#fff', 
    height: '100%' 
  }), []);
  
  // Memoized render function for action button
  const renderActionButton = useCallback((data) => {
    return (
      <Tooltip placement="right" title="View purchase order details">
        <Button
          onClick={() => openModal(data)}
          className="po-action-btn"
          icon="profile"
          size="small"
        />
      </Tooltip>
    );
  }, [openModal]);
  
  // Memoized render function for PO number
  const renderPoNumber = useCallback((val) => (
    <Tag color="blue" style={{ fontWeight: 600, fontSize: 12 }}>
      {val}
    </Tag>
  ), []);
  
  // Memoized render function for date
  const renderDate = useCallback((date) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <span style={{ fontWeight: 600, color: '#1890ff' }}>
        {moment(date).format('MMM DD, YYYY')}
      </span>
      <span style={{ fontSize: 10, color: '#8c8c8c' }}>
        {moment(date).fromNow()}
      </span>
    </div>
  ), []);
  
  // Memoized render function for customer
  const renderCustomer = useCallback((customer) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <div style={{ 
        width: 28, 
        height: 28, 
        borderRadius: '50%', 
        background: 'linear-gradient(135deg, #1890ff, #096dd9)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 12
      }}>
        {customer?.charAt(0)?.toUpperCase() || 'C'}
      </div>
      <span style={{ fontWeight: 500 }}>{customer}</span>
    </div>
  ), []);
  
  // Memoized render function for currency
  const renderCurrency = useCallback((currency) => (
    <Tag color={currency === 'USD' ? 'gold' : 'green'} style={{ fontWeight: 600 }}>
      {currency}
    </Tag>
  ), []);
  
  // Memoized table columns
  const tableColumns = useMemo(() => [
    {
      key: 'viewPo',
      align: 'center',
      width: 50,
      render: renderActionButton,
    },
    {
      key: 'status',
      title: 'STATUS',
      align: 'center',
      width: 130,
      render: d => <StatusBadge status={d.status} />,
      sorter: (a, b) => {
        const A = a.status ? a.status : '';
        const B = b.status ? b.status : '';
        return A.localeCompare(B);
      },
      filters: [
        { text: 'OPEN', value: 'OPEN' },
        { text: 'SERVED', value: 'SERVED' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      key: 'po_num',
      title: 'PO NO.',
      dataIndex: 'po_num',
      align: 'center',
      width: 130,
      render: renderPoNumber,
      sorter: (a, b) => (a.po_num || '').localeCompare(b.po_num || ''),
    },
    {
      key: 'date',
      title: 'DATE ISSUED',
      dataIndex: 'date',
      align: 'center',
      width: 120,
      render: renderDate,
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      defaultSortOrder: 'descend',
    },
    {
      key: 'customer',
      title: 'CUSTOMER',
      dataIndex: 'customerLabel',
      align: 'center',
      width: 200,
      render: renderCustomer,
      filters: customers.map(customer => ({
        text: customer.bi_name,
        value: customer.bi_name,
      })),
      onFilter: (value, record) => record.customerLabel === value,
      sorter: (a, b) => (a.customerLabel || '').localeCompare(b.customerLabel || ''),
    },
    {
      key: 'delivered',
      title: 'DELIVERY STATUS',
      align: 'center',
      width: 250,
      render: data => (
        <DeliveryProgress 
          totalItems={data.totalItems}
          totalDelivered={data.totalDelivered}
          totalQuantity={data.totalQuantity}
        />
      ),
      sorter: (a, b) => {
        const percentA = a.totalQuantity > 0 ? (a.totalDelivered / a.totalQuantity) : 0;
        const percentB = b.totalQuantity > 0 ? (b.totalDelivered / b.totalQuantity) : 0;
        return percentA - percentB;
      },
    },
    {
      key: 'currency',
      title: 'CURRENCY',
      dataIndex: 'currency',
      align: 'center',
      width: 90,
      render: renderCurrency,
      sorter: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
      filters: [
        { text: 'PHP', value: 'PHP' },
        { text: 'USD', value: 'USD' },
      ],
      onFilter: (value, record) => record.currency === value,
    },
  ], [renderActionButton, renderPoNumber, renderDate, renderCustomer, renderCurrency, customers]);
  
  // Memoized stat cards renderer
  const statCardsRenderer = useMemo(() => (
    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
      {statCards.map((card, idx) => (
        <Col key={idx} xs={12} sm={6}>
          <Card style={cardStyle} bodyStyle={{ padding: '18px 20px' }} bordered={false}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ 
                width: 46, 
                height: 46, 
                borderRadius: 12, 
                background: card.bg, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0, 
                '--gc': card.gc, 
                animation: 'po-glow 2.4s ease-in-out infinite' 
              }}>
                <Icon type={card.icon} style={{ fontSize: 20, color: card.color }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: 11, 
                  color: '#8c8c8c', 
                  fontWeight: 500, 
                  letterSpacing: '.3px', 
                  whiteSpace: 'nowrap' 
                }}>
                  {card.title.toUpperCase()}
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: card.color, 
                  lineHeight: 1.2 
                }}>
                  {card.value}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  ), [statCards, cardStyle]);
  
  // Memoized table header
  const tableHeader = useMemo(() => (
    <div style={{ 
      padding: '14px 20px 12px', 
      borderBottom: '1px solid #f0f5ff', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8,
      flexWrap: 'wrap'
    }}>
      <Icon type="shopping" style={{ color: '#1890ff', fontSize: 16, animation: 'po-beat 1.8s ease-in-out infinite' }} />
      <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>
        PURCHASE ORDERS
      </span>
      <Badge 
        count={stats.total} 
        style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }} 
        overflowCount={9999} 
      />
      {stats.open > 0 && (
        <Tag color="green" style={{ marginLeft: 4 }}>
          <Icon type="unlock" /> {stats.open} Open
        </Tag>
      )}
      {stats.served > 0 && (
        <Tag color="blue" style={{ marginLeft: 4 }}>
          <Icon type="check-circle" /> {stats.served} Served
        </Tag>
      )}
    </div>
  ), [stats.total, stats.open, stats.served]);
  
  // Memoized pagination config
  const paginationConfig = useMemo(() => ({
    total: pageLength,
    current: paginate?.page,
    pageSize: paginate?.pageSize,
    defaultPageSize: paginate?.pageSize,
    showSizeChanger: true,
    onChange: onPageChange,
    onShowSizeChange: onSizeChange,
    pageSizeOptions: ['10', '15', '25', '50'],
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
    position: 'both',
    style: { padding: '8px 16px', marginBottom: 0 }
  }), [pageLength, paginate, onPageChange, onSizeChange]);

  return (
    <div style={{ padding: '8px 0' }}>
      <style>{tableStyles}</style>
      
      {statCardsRenderer}

      <Card 
        bordered={false} 
        style={{ 
          borderRadius: 10, 
          boxShadow: '0 2px 12px rgba(24,144,255,0.07)', 
          border: '1px solid #e6f0ff' 
        }} 
        bodyStyle={{ padding: '0 0 8px 0' }}
      >
        {tableHeader}
        
        <Table
          rowKey="id"
          bordered
          size="middle"
          columns={tableColumns}
          dataSource={poList}
          style={{ margin: 0, overflowX: 'auto' }}
          rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'po-row-stripe'}
          pagination={paginationConfig}
        />
      </Card>
    </div>
  );
};

export default React.memo(PurchaseOrderTable);